import { useCtaCatalogs } from "@/hooks";
import type { CtaCatalogItemApi } from "@/lib/api";
import { NoRealData } from "@/components/shared";

/** Escalas oficiais em uso no CTA (GET /cta/catalogs), somente leitura. */
export default function CtaCatalogsPanel() {
  const catalogs = useCtaCatalogs();

  if (!catalogs) {
    return (
      <NoRealData
        icon="ri-error-warning-line"
        title="Escalas do CTA indisponíveis"
        description="As escalas de saúde (F), raiz (R), alvo (A) e prioridade (P) vêm do servidor e não puderam ser carregadas."
      />
    );
  }

  const h = catalogs.homologation;

  return (
    <div className="space-y-5">
      <div className="bg-caution-50 border border-caution-200 rounded-xl p-4 text-xs text-caution-800 space-y-1">
        <p className="font-semibold">Versão {h.version} · {h.status}</p>
        <p>Decisão: {h.decided_by} em {new Date(h.decided_on).toLocaleDateString("pt-BR")}. Registro formal: {h.formal_record}</p>
        <p>Origem das escalas: {h.scale_origin}</p>
        {h.not_included && <p>Fora desta versão: {h.not_included}</p>}
      </div>

      <CatalogTable title="Condição fitossanitária (F)" items={catalogs.phytosanitary} />
      <CatalogTable title="Condição de raiz e colo (R)" items={catalogs.root_classes} />
      <CatalogTable title="Alvo (A)" items={catalogs.target_classes} />

      {catalogs.priority_classes && catalogs.priority_classes.length > 0 && (
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-1">Prioridade de intervenção (P)</h3>
          {catalogs.priority_homologation && (
            <p className="text-xs text-caution-700 mb-3">
              {catalogs.priority_homologation.version} · {catalogs.priority_homologation.status}. {catalogs.priority_homologation.note}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Código</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Nível</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Prazo</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Primeira ação</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Responsável</th>
                </tr>
              </thead>
              <tbody>
                {catalogs.priority_classes.map((p) => (
                  <tr key={p.code} className="border-b border-background-100 align-top">
                    <td className="py-2.5 px-3 font-mono text-xs text-foreground-800">{p.code}</td>
                    <td className="py-2.5 px-3 text-foreground-900">{p.name}</td>
                    <td className="py-2.5 px-3 text-xs text-foreground-600 whitespace-nowrap">{p.deadline_label}</td>
                    <td className="py-2.5 px-3 text-xs text-foreground-600">{p.first_action}</td>
                    <td className="py-2.5 px-3 text-xs text-foreground-600">{p.responsible}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {catalogs.field_limits && (
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-1">Limites de campo da vistoria</h3>
          <p className="text-xs text-foreground-500 mb-3">
            {catalogs.field_limits.version} · {catalogs.field_limits.status}. Tolerância CAP × DAP:{" "}
            {Math.round(catalogs.field_limits.cap_dap_tolerance * 100)}%
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Campo</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Limite físico</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Faixa plausível</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(catalogs.field_limits.fields).map(([key, f]) => (
                  <tr key={key} className="border-b border-background-100">
                    <td className="py-2.5 px-3 text-foreground-900">{f.label}</td>
                    <td className="py-2.5 px-3 text-xs text-foreground-600 tabular-nums">
                      {f.min} – {f.max} {f.unit}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-foreground-600 tabular-nums">
                      {f.plausible_min != null || f.plausible_max != null
                        ? `${f.plausible_min ?? f.min} – ${f.plausible_max ?? f.max} ${f.unit}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CatalogTable({ title, items }: { title: string; items: CtaCatalogItemApi[] }) {
  return (
    <div className="bg-white rounded-xl border border-background-200/70 p-5">
      <h3 className="text-sm font-semibold text-foreground-900 mb-3">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Código</th>
              <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Classe</th>
              <th className="text-left py-2 px-3 text-xs font-medium text-foreground-500">Observação</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.code} className="border-b border-background-100 align-top">
                <td className="py-2.5 px-3 font-mono text-xs text-foreground-800">{i.code}</td>
                <td className="py-2.5 px-3 text-foreground-900">{i.name}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-600">{i.caution || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
